<script setup>
import ViewerIcon from './ViewerIcon.vue'
import { SKYBOX_PRESETS } from '../skybox-presets.js'

const props = defineProps({
  open: Boolean,
  fov: {
    type: Number,
    default: 75
  },
  speed: {
    type: Number,
    default: 24
  },
  moveSpeed: {
    type: Number,
    default: 24
  },
  pinchSpeed: {
    type: Number,
    default: 2.4
  },
  background: {
    type: String,
    default: '#000000'
  },
  skyboxEnabled: Boolean,
  skyboxPreset: {
    type: String,
    default: 'echopark'
  },
  canSetInitialView: Boolean,
  initialViewSaving: Boolean
})

const emit = defineEmits([
  'close',
  'update:fov',
  'update:speed',
  'update:move-speed',
  'update:pinch-speed',
  'update:background',
  'update:skyboxEnabled',
  'update:skyboxPreset',
  'reset-view',
  'set-initial-view'
])
const backgrounds = ['#000000', '#efefef', '#ffffff', '#dce6ff']

function updateNumber(name, event) {
  emit(name, Number(event.target.value))
}

function formatSpeedMultiplier(value) {
  const multiplier = (Number(value) || 24) / 24
  return Number.isInteger(multiplier) ? String(multiplier) : multiplier.toFixed(1)
}

function formatMoveSpeed(value) {
  const speed = Number(value)
  if (!Number.isFinite(speed)) return '24'
  return Number.isInteger(speed) ? String(speed) : speed.toFixed(1)
}

function formatPinchSpeed(value) {
  const speed = Number(value)
  return `${Number.isFinite(speed) ? speed.toFixed(1) : '2.4'}×`
}
</script>

<template>
  <section class="sheet sheet--compact sheet--adjust" :class="{ 'is-open': open }" :aria-hidden="!open">
    <button class="sheet__handle" type="button" aria-label="关闭" @click="$emit('close')">
      <ViewerIcon name="chevron-down" />
    </button>
    <header class="adjust-header">
      <div>
        <h2>显示参数</h2>
        <p>视角与背景</p>
      </div>
    </header>

    <div class="adjust-controls">
      <label class="adjust-range">
        <span>视野范围</span><output>{{ fov }}°</output>
        <input type="range" min="30" max="150" :value="fov" @input="updateNumber('update:fov', $event)" />
      </label>
      <label class="adjust-range">
        <span>旋转灵敏度</span><output>{{ formatSpeedMultiplier(speed) }}×</output>
        <input type="range" min="1" max="60" :value="speed" @input="updateNumber('update:speed', $event)" />
      </label>
      <label class="adjust-range">
        <span>飞行速度</span><output>{{ formatMoveSpeed(moveSpeed) }}</output>
        <input type="range" min="1" max="40" step="0.1" :value="moveSpeed" @input="updateNumber('update:move-speed', $event)" />
      </label>
      <label class="adjust-range">
        <span>缩放速率</span><output>{{ formatPinchSpeed(pinchSpeed) }}</output>
        <input type="range" min="0.1" max="5" step="0.1" :value="pinchSpeed" @input="updateNumber('update:pinch-speed', $event)" />
      </label>
    </div>

    <div class="reset-view-setting">
      <div>
        <strong>视角</strong>
        <span>恢复到模型进入时的初始视角</span>
      </div>
      <button type="button" @click="$emit('reset-view')">
        <ViewerIcon name="reset" />
        重置视角
      </button>
    </div>

    <div v-if="canSetInitialView" class="initial-view-setting">
      <div>
        <strong>初始视角</strong>
        <span>保存当前画面，之后进入模型时从这里开始</span>
      </div>
      <button
        type="button"
        :disabled="initialViewSaving"
        :aria-busy="initialViewSaving"
        @click="$emit('set-initial-view')"
      >
        <ViewerIcon name="camera" />
        {{ initialViewSaving ? '正在保存' : '设为初始视角' }}
      </button>
    </div>

    <div class="environment-setting">
      <div class="environment-heading">
        <strong>场景背景</strong>
        <span>{{ skyboxEnabled ? '环境场景' : '纯色背景' }}</span>
      </div>
      <div class="environment-tabs" role="tablist" aria-label="背景类型">
        <button
          type="button"
          role="tab"
          :class="{ active: !skyboxEnabled }"
          :aria-selected="!skyboxEnabled"
          @click="$emit('update:skyboxEnabled', false)"
        >纯色</button>
        <button
          type="button"
          role="tab"
          :class="{ active: skyboxEnabled }"
          :aria-selected="skyboxEnabled"
          @click="$emit('update:skyboxEnabled', true)"
        >天空盒</button>
      </div>

      <div v-if="!skyboxEnabled" class="color-panel" role="tabpanel">
        <div class="color-swatches">
          <button
            v-for="color in backgrounds"
            :key="color"
            type="button"
            :class="{ active: background === color }"
            :style="{ '--bg': color }"
            :aria-label="`切换为 ${color} 背景`"
            @click="$emit('update:background', color)"
          ><i></i></button>
        </div>
        <label class="background-color-picker" aria-label="自定义背景颜色">
          <span>自定义</span>
          <strong>{{ background.toUpperCase() }}</strong>
          <i :style="{ '--bg': background }"></i>
          <input
            type="color"
            :value="background"
            @input="$emit('update:background', $event.target.value)"
          />
        </label>
      </div>

      <div v-else class="skybox-strip" role="tabpanel">
        <div class="skybox-list">
          <button
            v-for="preset in SKYBOX_PRESETS"
            :key="preset.id"
            type="button"
            :class="{ active: skyboxPreset === preset.id }"
            :aria-pressed="skyboxPreset === preset.id"
            @click="$emit('update:skyboxPreset', preset.id)"
          >
            <span :style="{ backgroundImage: `url(${preset.url})` }"><i></i></span>
            <small>{{ preset.label }}</small>
          </button>
        </div>
        <p>横向滑动查看更多场景</p>
      </div>
    </div>
  </section>
</template>
